<template>
	<v-card elevation="1" rounded="lg" class="fill-height d-flex flex-column">
		<v-card-title class="d-flex align-center py-3 px-4">
			<v-icon color="pink" size="20" class="me-2">mdi-cake-variant</v-icon>
			<span class="text-subtitle-2">{{ $t("dashboard.birthdayWidget.title") }}</span>
		</v-card-title>

		<v-divider />

		<v-card-text class="flex-grow-1 pa-0">
			<!-- Loading -->
			<div v-if="loading" class="pa-4">
				<v-skeleton-loader v-for="i in 3" :key="i" type="list-item-two-line" class="mb-1" />
			</div>

			<!-- Empty state -->
			<div
				v-else-if="birthdays.length === 0"
				class="d-flex flex-column align-center justify-center pa-6"
				style="min-height: 140px"
			>
				<v-icon size="40" color="grey-lighten-1" class="mb-2">mdi-cake-variant-outline</v-icon>
				<span class="text-body-2 text-medium-emphasis">{{ $t("dashboard.birthdayWidget.noBirthdays") }}</span>
			</div>

			<!-- Birthday list (compact) -->
			<v-list v-else density="compact" class="py-0">
				<v-list-item
					v-for="person in displayBirthdays"
					:key="person.id"
					class="px-4 py-1"
					:class="{ 'today-birthday': person.isToday }"
				>
					<template #prepend>
						<BaseAvatar
							:text="getPersonInitials(person)"
							:backgroundColor="getPersonAvatarColor(person)"
							size="28"
							class="me-3"
						/>
					</template>

					<v-list-item-title class="text-body-2 font-weight-medium">
						<router-link
							:to="`/people/${person.id}`"
							class="text-decoration-none"
							style="color: inherit"
						>
							{{ getPersonFullName(person) }}
						</router-link>
					</v-list-item-title>

					<v-list-item-subtitle class="text-caption">
						<template v-if="person.isToday">
							<v-chip size="x-small" color="pink" variant="flat">
								{{ $t("dashboard.birthdayWidget.today") }}
							</v-chip>
							<span class="ms-1">{{ person.age }} {{ $t("common.years") }}</span>
						</template>
						<template v-else>
							{{ formatBirthdayLabel(person) }}
							<span class="mx-1">&middot;</span>
							{{ person.age }} {{ $t("common.years") }}
						</template>
					</v-list-item-subtitle>
				</v-list-item>
			</v-list>
		</v-card-text>

	</v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import {
	getUpcomingBirthdays,
	type BirthdayPerson,
} from "@/services/personService";
import BaseAvatar from "@/components/common/BaseAvatar.vue";
import { usePersonFormatting } from "@/composables/usePersonFormatting";
import { useDateFormat } from "@/composables/useDateFormat";

const MAX_ITEMS = 5;

const { t } = useI18n();
const { getPersonInitials, getPersonFullName, getPersonAvatarColor } = usePersonFormatting();
const { formatDateOnly } = useDateFormat();

const birthdays = ref<BirthdayPerson[]>([]);
const loading = ref(true);

const displayBirthdays = computed(() => birthdays.value.slice(0, MAX_ITEMS));

function formatBirthdayLabel(person: BirthdayPerson): string {
	if (person.daysUntilBirthday === 1) {
		return t("dashboard.birthdayWidget.tomorrow");
	}
	if (person.daysUntilBirthday <= 7) {
		return t("dashboard.birthdayWidget.inDays", { days: person.daysUntilBirthday });
	}
	return formatDateOnly(person.birthDate, { day: "2-digit", month: "2-digit" });
}

onMounted(async () => {
	try {
		birthdays.value = await getUpcomingBirthdays(30);
	} catch {
		birthdays.value = [];
	} finally {
		loading.value = false;
	}
});
</script>

<style scoped>
.today-birthday {
	background-color: rgba(233, 30, 99, 0.08);
	border-left: 3px solid rgb(233, 30, 99);
}
</style>
